// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { DndContext } from "@dnd-kit/core";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Filter, Loader2, Search, UnfoldHorizontal, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { toast } from "sonner";
import { ActiveFiltersDisplay } from "@/components/active-filters-display";
import { ColumnAliasModal } from "@/components/column-alias-modal";
import { CSVExportButton } from "@/components/csv-export-button";
import { DraggableTableHeader } from "@/components/draggable-table-header";
import { EntityFilterModal } from "@/components/entity-filter-modal";
import { ExportFilenameDialog } from "@/components/export-filename-dialog";
import ResetTableMenu from "@/components/reset-table-menu";
import { SalespeopleColumnSelector } from "@/components/salespeople-column-selector";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Input } from "@/components/ui/input";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { SortIcon } from "@/components/ui/sort-icon";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useEmployeeTeams } from "@/hooks/api/use-employee-teams";
import {
  type Salesperson,
  type SalespersonWithMetrics,
  useInfiniteSalespeople,
  useSalespeopleExport,
} from "@/hooks/api/use-salespeople";
import { useSalespersonTouchpointTypes } from "@/hooks/api/use-salesperson-metrics";
import { useOrganizationSessionStorage } from "@/hooks/use-browser-storage";
// Virtual scrolling will be added in future iteration
// import { useVirtualizer } from "@tanstack/react-virtual";
import { useSalespersonColumnSelection } from "@/hooks/use-column-selection";
import { usePaginatedCSVExport } from "@/hooks/use-csv-export";
import { useMetricCardFilters } from "@/hooks/use-metric-card-filters";
import { usePersistedColumnState } from "@/hooks/use-persisted-column-state";
import { useSalespersonFilterState } from "@/hooks/use-salesperson-filter-state";
import {
  useSalespersonListFilters,
  useSalespersonMetrics as useSalespersonMetricsConfig,
} from "@/hooks/use-salesperson-list-filters";
import { useSearchState } from "@/hooks/use-search-state";
import {
  constrainedClosestCenter,
  restrictToTableBounds,
} from "@/lib/collision-detection";
import type { ColumnAliases } from "@/lib/column-aliases";
import {
  createBulkColumnResizeHandler,
  createColumnResizeHandler,
  createSingleColumnResizeHandler,
} from "@/lib/column-resize-utils";
import { formatDateForFilename } from "@/lib/date-formatting";
import type { MetricsFilters } from "@/lib/filters";
import { cn } from "@/lib/utils";
import { MetricDrilldown } from "@/pages/salespeople/salesperson-explorer/metric-drilldown";
import type { SalespersonMetric } from "@/pages/salespeople/salesperson-explorer/salesperson-metrics-config";
import {
  DRILLDOWN_LIMIT,
  METRIC_CONFIG,
} from "@/pages/salespeople/salesperson-explorer/salesperson-metrics-config";
import type { SalespersonMetrics } from "@/types/salesperson-types";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import {
  getSalespersonColumnDefs,
  getSalespersonColumns,
} from "./salesperson-columns";

export function SalespeopleDataTable() {
  const { state, isMobile } = useSidebar();

  // State for sorting and searching - Replace useState search with sessionStorage
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: SortDirection;
  }>({
    key: "name",
    direction: "asc",
  });

  // Use sessionStorage for search state (tab-specific)
  const {
    searchInputValue,
    debouncedSearchQuery,
    handleSearchChange,
    clearSearch,
  } = useSearchState("salespeople::list::search");

  const [showPerformanceMessage, setShowPerformanceMessage] = useState(false);

  // State for metrics
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedDrilldown, setSelectedDrilldown] = useState<{
    salespersonId: string;
    metric: SalespersonMetric;
  } | null>(null);

  // Column aliases state management using sessionStorage (no longer persisted across sessions)
  const [columnAliases, setColumnAliases] =
    useOrganizationSessionStorage<ColumnAliases>(
      "salespeople::list::columns::aliases",
      {},
      {
        // Migrate from localStorage to sessionStorage
        migration: {
          from: {
            key: "salespeople::list::columns::aliases",
            storage: "localStorage",
            organizationScoped: true,
          },
          cleanup: true, // Remove from localStorage after migration
        },
      },
    );
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [aliasColumnId, setAliasColumnId] = useState<string>("");

  // Use the salesperson metrics configuration hook
  const { selectedMetrics, setSelectedMetrics, getDefaultMetrics } =
    useSalespersonMetricsConfig();

  // Use salesperson filter state for metric filters
  const salespersonFilterState = useSalespersonFilterState("salespeople::list");
  const {
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    clearAllFilters: clearAllMetricFilters,
  } = salespersonFilterState;

  const hasMetricFilters =
    hasAccountFilters || hasOpportunityFilters || hasTouchpointFilters;

  // Calculate total metric filter count
  const totalMetricFilterCount = useMemo(() => {
    const accountCount = Object.keys(accountFilters.filters || {}).length;
    const opportunityCount = Object.keys(
      opportunityFilters.filters || {},
    ).length;
    const touchpointCount = Object.keys(touchpointFilters.filters || {}).length;
    return accountCount + opportunityCount + touchpointCount;
  }, [accountFilters, opportunityFilters, touchpointFilters]);

  // Process metric filters for display using the same logic as Report Cards
  const {
    displayFilters: metricDisplayFilters,
    filterColumns: metricFilterColumns,
    handleClearFilter: handleClearMetricFilter,
  } = useMetricCardFilters(salespersonFilterState);

  // Hooks for data and features
  const { ref: loadMoreRef, inView } = useInView();
  const {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams,
  } = useSalespersonListFilters();

  // Reset sort to defaults
  const resetSortToDefaults = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });
  }, [getDefaultSortSettings]);

  // Handle clear all filters and reset sort - Updated for sessionStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    resetSortToDefaults();

    // Clear search state from sessionStorage
    clearSearch();
  }, [clearAllFilters, resetSortToDefaults, clearSearch]);

  // When component mounts, set sort from filters (search is now in sessionStorage)
  useEffect(() => {
    // Load sort configuration from localStorage
    if (filters.sortColumn) {
      setSortConfig({
        key: filters.sortColumn as string,
        direction: filters.sortDirection as SortDirection,
      });
    }
  }, [filters.sortColumn, filters.sortDirection]);

  // Search is now managed by useSearchState hook - no need for manual debouncing

  // Fetch employee teams for team filter options
  const { data: employeeTeams } = useEmployeeTeams();

  // Derive select options for filters
  const filterOptions = useMemo(() => {
    // Get team names from employee teams
    const teamNames = Array.isArray(employeeTeams)
      ? employeeTeams?.map((team) => team.display_name)
      : [];

    return {
      team_name: teamNames,
    } as const;
  }, [employeeTeams]);

  const handleMetricClick = useCallback(
    (salespersonId: string, metric: SalespersonMetric) => {
      setSelectedDrilldown({ salespersonId, metric });
    },
    [],
  );

  // Define columns for the table (following campaigns pattern exactly)
  const columns = useMemo<UnifiedTableColumn<Salesperson>[]>(() => {
    return getSalespersonColumns({
      selectedMetrics,
      filterOptions,
      onMetricDrilldown: handleMetricClick,
    });
  }, [selectedMetrics, filterOptions, handleMetricClick]);

  // Helper function to build query parameters (shared between main query and CSV export)
  const buildQueryParams = useCallback(
    (limit: number) => {
      const baseParams = getQueryParams({
        searchTerm: debouncedSearchQuery,
        columns: columns, // Use columns directly like campaigns
        limit,
      });

      const params = {
        ...baseParams,
        sort_by: sortConfig.key,
        sort_direction:
          sortConfig.direction === null ? undefined : sortConfig.direction,
        metrics: selectedMetrics.length > 0 ? selectedMetrics : undefined,
      };

      // Add metric filters if they exist
      const metricFilters = {
        ...(hasAccountFilters && {
          account_filters: accountFilters,
        }),
        ...(hasOpportunityFilters && {
          opportunity_filters: opportunityFilters,
        }),
        ...(hasTouchpointFilters && {
          touchpoint_filters: touchpointFilters,
        }),
      };

      if (Object.keys(metricFilters).length > 0) {
        params.metric_filters = metricFilters;
      }

      return params;
    },
    [
      getQueryParams,
      debouncedSearchQuery,
      columns, // Include columns in dependency array like campaigns
      selectedMetrics,
      sortConfig,
      hasAccountFilters,
      hasOpportunityFilters,
      hasTouchpointFilters,
      accountFilters,
      opportunityFilters,
      touchpointFilters,
    ],
  );

  // Get query parameters for API call
  const queryParams = useMemo(() => {
    return buildQueryParams(50);
  }, [buildQueryParams]);

  // Fetch data using infinite query
  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteSalespeople(queryParams);

  // Flatten the infinite query data with performance optimization
  const salespeople = useMemo(() => {
    if (!data || data.pages.length === 0) return [];

    // Use slice instead of flatMap if possible for better performance
    if (data.pages.length === 1) return data.pages[0].results;

    return data.pages.flatMap((page) => page.results) || [];
  }, [data]);

  // Show performance message after 5 seconds of loading
  useEffect(() => {
    if (isLoading && selectedMetrics.length > 0) {
      const timer = setTimeout(() => {
        setShowPerformanceMessage(true);
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    } else {
      setShowPerformanceMessage(false);
    }
  }, [isLoading, selectedMetrics.length]);

  // Column state management with resizing and ordering
  const {
    columnOrder,
    setColumnOrder,
    columnSizing,
    setColumnSizing,
    pinnedColumns,
    toggleColumnPin,
    wrappedColumns,
    setWrappedColumns,
    toggleColumnWrap,
    dndState: { sensors, handleDragEnd },
    resetToDefaultPinned,
    getInitializeColumnOrder,
  } = usePersistedColumnState("salespeople::list::columns", {
    getDefaultPinnedColumns: () => ["name"],
  });

  const {
    columnVisibility,
    setColumnVisibility,
    resetToDefault: baseResetColumnVisibility,
  } = useSalespersonColumnSelection(columns);

  // Reset column visibility **and** metric selection to defaults
  const resetColumnVisibility = useCallback(() => {
    baseResetColumnVisibility();
    setSelectedMetrics(getDefaultMetrics());
  }, [baseResetColumnVisibility, setSelectedMetrics, getDefaultMetrics]);

  // Comprehensive table reset function

  // Reset only column widths
  const resetColumnWidths = useCallback(() => {
    setColumnSizing({});
  }, [setColumnSizing]);

  // Alias handlers
  const handleSetAlias = useCallback((columnId: string) => {
    setAliasColumnId(columnId);
    setShowAliasModal(true);
  }, []);

  const handleSaveAlias = useCallback(
    (columnId: string, alias: string) => {
      setColumnAliases((prev) => ({ ...prev, [columnId]: alias }));
    },
    [setColumnAliases],
  );

  const handleClearAlias = useCallback(
    (columnId: string) => {
      setColumnAliases((prev) => {
        const newAliases = { ...prev };
        delete newAliases[columnId];
        return newAliases;
      });
    },
    [setColumnAliases],
  );

  const handleResetAliases = useCallback(() => {
    setColumnAliases({});
  }, [setColumnAliases]);

  // Convert to TanStack columnDefs with visibility filtering
  const columnDefs = useMemo<ColumnDef<Salesperson>[]>(() => {
    const allDefs = getSalespersonColumnDefs({
      selectedMetrics,
      filterOptions,
      onMetricDrilldown: handleMetricClick,
    });

    const visibleDefs = allDefs
      .map((col) => ({ ...col, enableResizing: true }))
      .filter((col) => columnVisibility[col.id as string] !== false);

    return visibleDefs;
  }, [selectedMetrics, filterOptions, columnVisibility, handleMetricClick]);

  // Reset only column order (preserve pinned columns at the start)
  const resetColumnOrder = useCallback(() => {
    setColumnOrder((prev) => {
      const defaultOrder = columnDefs.map((c) => c.id!);
      const pinnedFirst = prev.filter((id) => pinnedColumns.includes(id));
      const unpinnedDefault = defaultOrder.filter(
        (id) => !pinnedColumns.includes(id),
      );
      return [...pinnedFirst, ...unpinnedDefault];
    });
  }, [setColumnOrder, columnDefs, pinnedColumns]);

  // Reset pinned columns
  const resetPinnedColumns = useCallback(() => {
    resetToDefaultPinned();
  }, [resetToDefaultPinned]);

  // Reset wrapped columns
  const resetWrappedColumns = useCallback(() => {
    setWrappedColumns([]);
  }, [setWrappedColumns]);

  // Reset sort order to defaults
  const resetSortOrder = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });
    updateFilter("sortColumn", defaults.sortColumn);
    updateFilter("sortDirection", defaults.sortDirection);
  }, [getDefaultSortSettings, updateFilter]);

  // Comprehensive table reset function
  const handleResetTable = useCallback(() => {
    // Reset all filters and search
    handleClearAllFilters();

    // Reset metric filters (account, opportunity, touchpoint)
    clearAllMetricFilters();

    // Reset column visibility to default
    resetColumnVisibility();

    // Reset column order to default (empty array will use default order)
    setColumnOrder([]);

    // Reset column sizing to default
    setColumnSizing({});

    // Reset pinned columns to default state
    resetPinnedColumns();

    // Reset column aliases
    setColumnAliases({});

    // Reset wrapped columns
    resetWrappedColumns();

    // Show success toast
    toast.success("Table reset to default state");
  }, [
    handleClearAllFilters,
    clearAllMetricFilters,
    resetColumnVisibility,
    setColumnOrder,
    setColumnSizing,
    resetPinnedColumns,
    setColumnAliases,
    resetWrappedColumns,
  ]);

  // Initialize column order when columnDefs change
  useEffect(() => {
    const initializeOrder = getInitializeColumnOrder(columnDefs);
    initializeOrder();
  }, [columnDefs, getInitializeColumnOrder]);

  // CSV Export functionality
  const { exportSalespeopleAsync } = useSalespeopleExport();
  const { exportData, isExporting, defaultFilename } =
    usePaginatedCSVExport<SalespersonWithMetrics>({
      fetchAllData: async () => {
        const baseParams = buildQueryParams(50);
        return exportSalespeopleAsync(baseParams);
      },
      columns: columns,
      filename: `salespeople_${formatDateForFilename(new Date())}`,
    });

  // State for export filename dialog
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const handleExport = (filename: string) => {
    exportData(filename);
  };

  // Get total count from the first page (since total is the same across all pages)
  const totalSalespeopleCount = data?.pages[0]?.total ?? 0;

  // Auto-fetch next page when scrolling
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Create table instance
  const table = useReactTable({
    data: salespeople,
    columns: columnDefs,
    state: {
      columnOrder,
      columnSizing,
    },
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    enableSorting: true,
    defaultColumn: {
      minSize: 80,
      size: 150,
    },
    debugTable: false,
  });

  // Handle sort
  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      const column = columns.find((col) => col.key === key);
      const currentDirection = prev.key === key ? prev.direction : null;

      let newDirection: SortDirection;
      if (currentDirection === null) {
        newDirection = column?.defaultSortDirection || "asc";
      } else if (currentDirection === "asc") {
        newDirection = "desc";
      } else {
        newDirection = "asc";
      }

      // Update filters
      updateFilter("sortColumn", key);
      updateFilter("sortDirection", newDirection);

      return { key, direction: newDirection };
    });
  };

  // Render sort icon
  const renderSortIcon = (column: UnifiedTableColumn<Salesperson>) => {
    if (!column.sortable) return null;

    const currentSort =
      sortConfig.key === column.key ? sortConfig.direction : null;
    const isActive = sortConfig.key === column.key;
    return (
      <SortIcon isActive={isActive} direction={currentSort} className="ml-1" />
    );
  };

  // Search function is now provided by useSearchState hook

  // Handle double-click on column resize handle to auto-size
  const handleDoubleClickResize = useCallback(
    createColumnResizeHandler(setColumnSizing, "salespeople-table"),
    [],
  );

  // Single column resize handler for kebab menu
  const handleSingleColumnResize = useCallback(
    createSingleColumnResizeHandler(setColumnSizing, "salespeople-table"),
    [],
  );

  // Bulk resize handler for autosize all button
  const handleBulkColumnResize = useCallback(
    createBulkColumnResizeHandler(setColumnSizing, "salespeople-table"),
    [],
  );

  // Handle removing a column from view
  const handleRemoveColumn = useCallback(
    (columnId: string) => {
      // Find the column to check its type
      const column = columns.find((col) => col.key === columnId);

      // For metric columns, remove from selectedMetrics
      if (column?.columnType === "metric") {
        // Extract metric key from column ID following our naming convention
        // Column IDs are prefixed for uniqueness: "metric_accounts_engaged"
        // But selectedMetrics state and API expect unprefixed keys: "accounts_engaged"
        const metricKey = columnId.replace("metric_", "") as SalespersonMetric;
        setSelectedMetrics((prev) => prev.filter((m) => m !== metricKey));
      } else {
        // For all other columns (core, custom), update visibility
        setColumnVisibility((prev) => ({
          ...prev,
          [columnId]: false,
        }));
      }
    },
    [columns, setColumnVisibility, setSelectedMetrics],
  );

  // Memoize pinnedColumnPositions calculation at component level to avoid React hooks rules violation
  const pinnedColumnPositions = useMemo(() => {
    const positions: Record<string, number> = {};
    let cumulativeLeft = 0;

    columnOrder.forEach((colId) => {
      if (pinnedColumns.includes(colId)) {
        const column = table.getColumn(colId);
        if (column) {
          positions[colId] = cumulativeLeft;
          cumulativeLeft += column.getSize();
        }
      }
    });

    return positions;
  }, [columnOrder, pinnedColumns, table]);

  // Render loading state
  const renderLoadingStateTable = () => {
    // Using columnDefs now for structure during loading
    const headerGroups = table.getHeaderGroups();

    return (
      <>
        <TableHeader>
          {headerGroups.map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-muted/50">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} style={{ width: header.getSize() }}>
                  <Skeleton className="h-4 w-full" />
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {Array.from({ length: 7 }).map((_, i) => (
            <TableRow key={i}>
              {i === 3 && showPerformanceMessage ? (
                <TableCell
                  colSpan={headerGroups[0].headers.length}
                  className="py-6"
                >
                  <div className="flex items-center">
                    <div
                      role="status"
                      aria-live="polite"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-muted/50 border border-muted-foreground/10 animate-in fade-in duration-500"
                    >
                      <svg
                        className="w-4 h-4 text-primary flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      <span className="text-sm font-medium text-muted-foreground">
                        To speed up query time, try adding metric filters or
                        reducing the number of metric columns
                      </span>
                    </div>
                  </div>
                </TableCell>
              ) : (
                headerGroups[0].headers.map((header) => (
                  <TableCell key={`${i}-${header.id}`}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))
              )}
            </TableRow>
          ))}
        </TableBody>
      </>
    );
  };

  // Render table content
  const renderTableContent = () => {
    const rowModel = table.getRowModel();

    return (
      <>
        <DraggableTableHeader
          table={table}
          columnOrder={columnOrder}
          handleSort={handleSort}
          sortConfig={sortConfig}
          renderSortIcon={renderSortIcon}
          filters={filters}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
          columns={columns}
          handleDoubleClickResize={handleDoubleClickResize}
          pinnedColumns={pinnedColumns}
          onTogglePin={toggleColumnPin}
          wrappedColumns={wrappedColumns}
          onToggleWrap={toggleColumnWrap}
          onResizeToFit={handleSingleColumnResize}
          onRemoveColumn={handleRemoveColumn}
          columnAliases={columnAliases}
          onSetAlias={handleSetAlias}
        />
        <TableBody>
          {isError ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                Error loading salespeople: Please try again.
              </TableCell>
            </TableRow>
          ) : salespeople.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                "No matching salespeople"
              </TableCell>
            </TableRow>
          ) : (
            <>
              {rowModel.rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const isPinned = pinnedColumns.includes(cell.column.id);
                    const isWrapped = wrappedColumns.includes(cell.column.id);
                    const pinnedLeft = pinnedColumnPositions[cell.column.id];

                    return (
                      <TableCell
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.getSize(),
                          maxWidth: cell.column.getSize(),
                          ...(isPinned && { left: `${pinnedLeft}px` }),
                        }}
                        className={cn(
                          cell.column.id === sortConfig.key && "bg-muted/40",
                          isPinned && "pinned-column bg-background",
                        )}
                        data-column-id={cell.column.id}
                      >
                        <div
                          className={cn(
                            "cell-content",
                            isWrapped && "wrapped",
                            !isWrapped && "inline-block w-full",
                          )}
                          title={String(row.getValue(cell.column.id) || "")}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {/* Load more trigger element */}
              {hasNextPage && (
                <TableRow ref={loadMoreRef}>
                  <TableCell
                    colSpan={table.getAllColumns().length}
                    className="h-16 text-center"
                  >
                    {isFetchingNextPage ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span className="text-sm text-muted-foreground">
                          Loading more salespeople...
                        </span>
                      </div>
                    ) : (
                      <div className="py-4">
                        <span className="text-sm text-muted-foreground">
                          Loading more...
                        </span>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </>
          )}
        </TableBody>
      </>
    );
  };

  return (
    <Card className="h-full relative flex flex-col">
      {/* Fixed position loading indicator */}
      {isFetchingNextPage && (
        <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-10 bg-background/90 shadow-md rounded-md px-4 py-2 border">
          <div className="flex items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="ml-2">Loading more...</span>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Filter by salesperson name..."
              className="w-[250px] border-0 border-b border-border shadow-none rounded-none pl-7 pr-8 h-10 placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus:border-muted-foreground transition-colors bg-transparent hover:border-muted-foreground"
              onChange={(e) => handleSearchChange(e.target.value)}
              value={searchInputValue}
              aria-label="Search salespeople by name"
              role="searchbox"
            />
            {searchInputValue && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                onClick={clearSearch}
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground/80" />
              </Button>
            )}
          </div>
          <InfoTooltip
            trigger={
              <button
                className="ml-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Wildcard search info"
                onClick={(e) => e.stopPropagation()}
              >
                <InfoCircledIcon className="h-3.5 w-3.5" />
              </button>
            }
            side="right"
            sideOffset={5}
          >
            <div className="text-sm space-y-2">
              <p className="font-medium">Supports wildcards</p>
              <ul className="space-y-1">
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">
                    _
                  </code>{" "}
                  matches a single character
                </li>
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">
                    %
                  </code>{" "}
                  matches zero or more characters
                </li>
              </ul>
            </div>
          </InfoTooltip>
        </div>
        <div className="flex items-center gap-2">
          {/* Record Count - First item in right-side group */}
          <div className="text-sm text-muted-foreground">
            {isLoading
              ? "Loading..."
              : totalSalespeopleCount > 0
                ? `${totalSalespeopleCount.toLocaleString()} matching ${
                    totalSalespeopleCount === 1 ? "salesperson" : "salespeople"
                  }`
                : "No matching salespeople"}
          </div>

          {/* Salespeople Column & Metrics Selector */}
          <SalespeopleColumnSelector
            selectedMetrics={selectedMetrics}
            onSelectMetrics={setSelectedMetrics}
            showMetricsSection={true}
            defaultMetrics={getDefaultMetrics()}
          />

          {/* Metric Filters Button - Only show when metrics are selected */}
          {selectedMetrics.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilterModal(true)}
              className="flex items-center gap-1.5"
            >
              <Filter
                className={cn(
                  "h-4 w-4",
                  hasMetricFilters ? "text-primary" : "text-muted-foreground",
                )}
              />
              <span>Metric Filters</span>
              {totalMetricFilterCount > 0 && (
                <Badge className="ml-1 bg-primary text-primary-foreground">
                  {totalMetricFilterCount}
                </Badge>
              )}
            </Button>
          )}

          {/* Autosize All Columns Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  const visibleColumnIds = columnOrder.filter(
                    (columnId) => columnVisibility[columnId] !== false,
                  );
                  handleBulkColumnResize(visibleColumnIds);
                }}
                className="h-8 w-8"
              >
                <UnfoldHorizontal className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Resize all visible columns to fit their content</p>
            </TooltipContent>
          </Tooltip>

          {/* Reset Table Menu */}
          <ResetTableMenu
            onResetWidths={resetColumnWidths}
            onResetOrder={resetColumnOrder}
            onResetColumns={resetColumnVisibility}
            onResetTableFilters={handleClearAllFilters}
            onResetMetricFilters={clearAllMetricFilters}
            onResetPinnedColumns={resetPinnedColumns}
            onResetSort={resetSortOrder}
            onResetAliases={handleResetAliases}
            onResetWrappedColumns={resetWrappedColumns}
            onResetAll={handleResetTable}
          />
          <ExportFilenameDialog
            open={isExportDialogOpen}
            onOpenChange={setIsExportDialogOpen}
            defaultFilename={defaultFilename}
            extension="csv"
            onExport={handleExport}
            disabled={isExporting || salespeople.length === 0}
          >
            <CSVExportButton
              onClick={() => setIsExportDialogOpen(true)}
              isExporting={isExporting}
              disabled={isExporting || salespeople.length === 0}
              tooltipText="Export salespeople data to CSV"
            />
          </ExportFilenameDialog>
        </div>
      </div>

      {/* Active Filters Display – reserve 32px height */}
      <div className="px-4 pb-2 space-y-2 min-h-8 flex-shrink-0">
        {/* Table-level filters */}
        {hasFilters && (
          <div className="flex items-center justify-between">
            <ActiveFiltersDisplay
              filters={
                // Filter out sort-related properties from display
                Object.fromEntries(
                  Object.entries(filters).filter(
                    ([key]) => key !== "sortColumn" && key !== "sortDirection",
                  ),
                )
              }
              columns={columns}
              clearFilter={clearFilter}
              clearAllFilters={handleClearAllFilters}
              clearAllText="Clear all table filters"
              filterType="table"
            />
          </div>
        )}

        {/* Metric filters */}
        {Object.keys(metricDisplayFilters).length > 0 && (
          <div className="flex items-center justify-between">
            <ActiveFiltersDisplay
              filters={metricDisplayFilters}
              columns={metricFilterColumns}
              clearFilter={handleClearMetricFilter}
              clearAllFilters={clearAllMetricFilters}
              clearAllText="Clear all metric filters"
              filterType="metric"
            />
          </div>
        )}
      </div>

      {/* Filter Modal */}
      <EntityFilterModal
        open={showFilterModal}
        onOpenChange={setShowFilterModal}
        scope="salespeople::list"
        entity="salesperson"
      />

      {/* Main Table Container */}
      <DndContext
        sensors={sensors}
        collisionDetection={constrainedClosestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToTableBounds]}
      >
        <div
          className={cn(
            "rounded-md border-t border-x overflow-y-auto flex-grow",
            table.getState().columnSizingInfo.isResizingColumn &&
              "table-resizing",
          )}
          style={{
            maxWidth: isMobile
              ? "calc(100vw - 2rem)"
              : state === "collapsed"
                ? "calc(100vw - var(--sidebar-width-icon) - 2rem)"
                : "calc(100vw - var(--sidebar-width) - 2rem)",
            minWidth: "640px", // Prevent table from becoming too narrow on small screens
          }}
        >
          <Table
            className="w-full table-fixed"
            style={{ width: table.getTotalSize() }}
            data-table-id="salespeople-table"
            data-table-container
          >
            {isLoading ? renderLoadingStateTable() : renderTableContent()}
          </Table>
        </div>
      </DndContext>
      {selectedDrilldown &&
        (() => {
          const salesperson = salespeople.find(
            (sp) => sp.person_id === selectedDrilldown.salespersonId,
          );
          return salesperson ? (
            <MetricDrilldownWrapper
              salesperson={salesperson}
              metric={selectedDrilldown.metric}
              onClose={() => setSelectedDrilldown(null)}
              filters={{
                ...(hasAccountFilters && {
                  account_filters: accountFilters,
                }),
                ...(hasOpportunityFilters && {
                  opportunity_filters: opportunityFilters,
                }),
                ...(hasTouchpointFilters && {
                  touchpoint_filters: touchpointFilters,
                }),
              }}
            />
          ) : null;
        })()}

      {/* Column Alias Modal */}
      <ColumnAliasModal
        open={showAliasModal}
        onOpenChange={setShowAliasModal}
        columnId={aliasColumnId}
        originalTitle={(() => {
          const column = columns.find((col) => col.key === aliasColumnId);
          return typeof column?.title === "string"
            ? column.title
            : aliasColumnId;
        })()}
        originalDescription={(() => {
          if (aliasColumnId.startsWith("metric_")) {
            const metricKey = aliasColumnId.replace("metric_", "");
            return METRIC_CONFIG[metricKey as keyof typeof METRIC_CONFIG]
              ?.tooltip;
          }
          return undefined;
        })()}
        currentAlias={columnAliases[aliasColumnId]}
        onSave={handleSaveAlias}
        onClear={handleClearAlias}
      />
    </Card>
  );
}

const METRIC_TO_BASE: Record<string, keyof SalespersonMetrics> = {
  opportunities_influenced_amount: "opportunities_influenced",
  opportunities_influenced_won_amount: "opportunities_influenced_won",
  opportunities_influenced_in_cycle_amount: "opportunities_influenced_in_cycle",
  opportunities_created_post_engagement_amount:
    "opportunities_created_post_engagement",
  engagements_per_account: "accounts_engaged",
  engagements_per_person: "people_engaged",
  engagements_per_deal_contact: "deal_contacts_engaged",
};

function MetricDrilldownWrapper({
  salesperson,
  metric,
  onClose,
  filters,
}: {
  salesperson: Salesperson;
  metric: SalespersonMetric;
  onClose: () => void;
  filters: MetricsFilters;
}) {
  const drilldownMetric =
    (METRIC_TO_BASE[metric] as SalespersonMetric) || metric;
  const { data: typesData } = useSalespersonTouchpointTypes(
    salesperson.person_id,
  );

  // Use the existing metrics from the cached salesperson object
  const metrics = salesperson.metrics as SalespersonMetrics;

  // Create loading states (not loading since we already have the data)
  const loadingStates = useMemo(() => {
    const states = {} as Record<
      SalespersonMetric,
      { isLoading: boolean; isError: boolean }
    >;
    // Set all metrics as loaded since we have the data
    Object.keys(METRIC_CONFIG).forEach((metricKey) => {
      states[metricKey as SalespersonMetric] = {
        isLoading: false,
        isError: false,
      };
    });
    return states;
  }, []);

  return (
    <MetricDrilldown
      open={true}
      onOpenChange={(open) => !open && onClose()}
      personId={salesperson.person_id}
      metric={drilldownMetric}
      touchpointTypes={typesData || []}
      filters={filters}
      metrics={metrics}
      loadingStates={loadingStates}
      drillDownLimit={DRILLDOWN_LIMIT}
    />
  );
}
